import { Button, Link, Stack, Text } from '@ltvco/refresh-lib/theme';
import { DateUtil } from '@ltvco/refresh-lib/utils';
import { RecentReportSerializer } from '@ltvco/refresh-lib/v1';
import { LizanoListInfo, RecentReportSerialized } from '@ltvco/refresh-lib/v2';
import { AccessTime, InsertDriveFileOutlined } from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { mapReportTypeFromRecentReportsToChatType } from 'components/ChatBV/utils';
import { theme } from 'theme';

import { MoreWaysSectionItem } from '../MoreWaysSection';

import type { ReactElement } from 'react';

export const dateParser = new DateUtil();

export const getListReportCount = (list: LizanoListInfo) =>
  list.list_reports?.filter((item) => item.report).length || 0;

export const moreWaysItemsForEmptyState = (
  navigate: (path: string) => void,
  basePath = '/chatbv/chat'
): MoreWaysSectionItem[] => [
  {
    id: 'recent-reports1',
    icon: (
      <AccessTime fontSize="small" sx={{ color: theme.palette.primary.main }} />
    ),
    children: (
      <Text variant="body2">
        Add from your{' '}
        <Link
          component="button"
          onClick={() => navigate(`${basePath}/library/Recent%20Reports`)}
          sx={{ verticalAlign: 'baseline' }}
        >
          recent reports.
        </Link>
      </Text>
    ),
  },
  {
    id: 'recent-reports2',
    icon: (
      <InsertDriveFileOutlined
        fontSize="small"
        sx={{ color: theme.palette.primary.main }}
      />
    ),
    children: (
      <Text variant="body2">
        {' '}
        Directly from a new{' '}
        <Link
          component="button"
          onClick={() => navigate(basePath)}
          sx={{ verticalAlign: 'baseline' }}
        >
          report.
        </Link>
      </Text>
    ),
  },
];

export type HeaderButtonElementRecentReportsParams = {
  navigate: (path: string) => void;
  isMobile: boolean;
  breadcrumbTitle: string;
  onSettingsClick?: () => void;
  onAddClick?: () => void;
  onImportClick?: () => void;
};

export const headerButtonElementRecentReports = ({
  navigate,
  isMobile,
  breadcrumbTitle,
  onAddClick,
}: HeaderButtonElementRecentReportsParams): ReactElement | undefined => {
  if (breadcrumbTitle === 'Recent Reports') {
    return (
      <Stack
        direction="row"
        spacing={2}
        width="100%"
        justifyContent={isMobile ? 'space-between' : 'flex-end'}
      >
        <Button
          variant="contained"
          startIcon={<AutoAwesomeIcon />}
          sx={{ width: isMobile ? '100%' : 'auto' }}
          onClick={() => (onAddClick ? onAddClick() : navigate('/chatbv/chat'))}
        >
          New Chat
        </Button>
      </Stack>
    );
  }

  // TODO: Uncomment when settings feature is activated
  // if (breadcrumbTitle === 'Monitored Reports') {
  //   return (
  //     <Stack
  //       direction="row"
  //       spacing={2}
  //       justifyContent={isMobile ? 'space-between' : 'flex-end'}
  //       width="100%"
  //     >
  //       <Button
  //         variant="outlined"
  //         startIcon={<SettingsIcon />}
  //         sx={{ width: isMobile ? '100%' : 'auto' }}
  //         onClick={() => onSettingsClick?.()}
  //       >
  //         Settings
  //       </Button>
  //     </Stack>
  //   );
  // }

  // TODO: Uncomment when add/import features are activated
  // if (breadcrumbTitle === 'Saved Reports') {
  //   return (
  //     <Stack
  //       direction="row"
  //       spacing={2}
  //       justifyContent={isMobile ? 'space-between' : 'flex-end'}
  //       width="100%"
  //     >
  //       <Button
  //         variant="contained"
  //         startIcon={<AddIcon />}
  //         sx={{ width: isMobile ? '50%' : 'auto' }}
  //         onClick={() => onAddClick?.()}
  //       >
  //         Add
  //       </Button>
  //       <Button
  //         variant="outlined"
  //         startIcon={<FileUploadIcon />}
  //         sx={{ width: isMobile ? '50%' : 'auto' }}
  //         onClick={() => onImportClick?.()}
  //       >
  //         Import
  //       </Button>
  //     </Stack>
  //   );
  // }
};

export function serializeListReports(
  lists: LizanoListInfo[]
): RecentReportSerialized[] {
  const rows = lists.flatMap((list) => list.list_reports ?? []);
  return rows
    .filter((meta) => meta.report)
    .map(
      (meta) => RecentReportSerializer(meta.report!) as RecentReportSerialized
    );
}

export function listReportRemovePayloads(listInfos: LizanoListInfo[]): Array<{
  list_code: string;
  listReportId: number;
  reportPermalink: string;
}> {
  return listInfos.flatMap((list) =>
    (list.list_reports ?? [])
      .filter((meta) => meta.report)
      .map((meta) => {
        const serialized = RecentReportSerializer(
          meta.report!
        ) as RecentReportSerialized;
        return {
          list_code: list.list_code,
          listReportId: meta.id,
          reportPermalink: serialized.id,
        };
      })
  );
}

export const handleReportLibraryClick = (
  type: string,
  permalink: string,
  logError: (message: string, error: Error) => void,
  openReportFromReportLibrary: (permalink: string, mappedType: string) => void
) => {
  if (!permalink || !type) {
    logError(
      'Error in ListViewComponent handleReportLibraryClick: missing permalink or type',
      new Error(`permalink: ${permalink}, type: ${type}`)
    );
    return;
  }
  const mappedType = mapReportTypeFromRecentReportsToChatType(type);
  if (!mappedType) {
    logError(
      'Error mapping report type',
      new Error(`Could not map report type: ${type}`)
    );
    return;
  }
  openReportFromReportLibrary(permalink, mappedType);
};

export const handlePageChange = (
  page: number,
  setQueryParams: (queryParams: any) => void
) => {
  setQueryParams({ page });
  window.scrollTo(0, 0);
};
